import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { useCallback, useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

import type { ChatState } from '../useChatStore';
import { useChatStore } from '../useChatStore';
import type { PageContext } from './usePageContext';

/**
 * Agent-mode chat adaptor.
 *
 * Web-side adaptor for the read-only Agent backend (`services/ai-gateway`). It
 * owns a DEDICATED Socket.IO connection to the gateway (separate from the
 * existing platform-knowledge chat socket, which talks to the ai-engine) and
 * maps the gateway's clean event contract — `message_chunk` / `tool_call_start`
 * / `tool_call_complete` / `message_complete` / `error` (camelCase, no
 * `session_id`; the session is owned server-side per (user, org)) — onto the
 * shared chat store. Because the socket is dedicated to this turn's agent, no
 * client-side `session_id` filtering is needed.
 *
 * Auth: the org-scoped Auth0 access token is sent in the handshake AND with each
 * `send_message` (the gateway re-verifies a refreshed per-turn token), covering
 * the 30-min token lifetime without a reconnect.
 */

// Gateway event payloads — mirror services/ai-gateway `ChatServerEvent`.
interface MessageChunkEvent {
  delta: string;
}
interface ThinkingEvent {
  text: string;
  redacted?: boolean;
  parentToolUseId?: string | null;
}
interface ToolCallStartEvent {
  toolUseId: string;
  name: string;
  input: unknown;
  parentToolUseId?: string | null;
  subagentType?: string;
}
interface ToolCallCompleteEvent {
  toolUseId: string;
  name: string;
  isError: boolean;
  result: string;
  resultTruncated?: boolean;
  parentToolUseId?: string | null;
}
interface ToolCallDeniedEvent {
  toolUseId: string;
  name: string;
  reasonCode: string;
  parentToolUseId?: string | null;
}
interface FormPrefillProposalEvent {
  proposalId: string;
  entityType: string;
  fields: { key: string; label: string; value: unknown; reason?: string }[];
}
interface MessageCompleteEvent {
  stopReason: string | null;
  // LLM-Obs feedback-correlation ids (AID-236), present on a successful,
  // telemetry-on turn. Captured into the message's metadata so MessageFeedback
  // renders + submits them; absent otherwise (feedback stays hidden).
  traceId?: string;
  spanId?: string;
  mlApp?: string;
  responseId?: string;
}
interface GatewayErrorEvent {
  error: string;
}

/** Map the gateway's opaque error codes to user-facing copy. */
const agentErrorMessage = (code: string): string => {
  switch (code) {
    case 'rate_limited':
      return 'You are sending messages too quickly — please wait a moment and try again.';
    case 'message_too_long':
      return 'That message is too long. Please shorten it and try again.';
    case 'session_conflict':
      return 'Your organisation context changed. Please reopen the agent to continue.';
    case 'unauthorized':
      return 'Your session has expired. Please refresh the page and try again.';
    default:
      return 'The agent ran into a problem. Please try again.';
  }
};

export const useAgentChat = () => {
  const { getAccessTokenSilently } = useRisksmartUser();
  const socketRef = useRef<Socket | null>(null);
  // "New chat" flag. The agent session is owned server-side per (user, org), so
  // clearing the UI transcript alone leaves the next turn resuming the previous
  // conversation (AID-331). We instead ask the gateway to start a fresh session
  // on the NEXT turn — consumed once, on the next `send_message`, which avoids
  // any dependency on the socket being connected at click time.
  const pendingNewSessionRef = useRef(false);
  // Settles the in-flight turn's promise (detaching its socket handlers) without
  // surfacing an error. Set for the lifetime of a turn, cleared when it ends.
  const cancelTurnRef = useRef<(() => void) | null>(null);

  /** Mark the next agent turn as starting a fresh server-side session. */
  const resetAgentSession = useCallback((): void => {
    pendingNewSessionRef.current = true;
  }, []);

  /**
   * Abort the in-flight turn on user intent ("New chat"), NOT as a failure.
   * Detaches the turn's socket handlers first so the follow-up disconnect stays
   * silent (no `agent_unavailable` error), then drops the socket so the aborted
   * turn's remaining server events can't bleed into the fresh conversation. A
   * no-op when nothing is in flight. The next turn reconnects.
   */
  const cancelAgentTurn = useCallback((): void => {
    const cancel = cancelTurnRef.current;
    if (!cancel) {
      return;
    }
    cancel();
    socketRef.current?.disconnect();
    socketRef.current = null;
  }, []);

  /** Connect (or reuse) the dedicated gateway socket, authed with a fresh token. */
  const connect = useCallback(async (): Promise<Socket> => {
    const existing = socketRef.current;
    if (existing?.connected) {
      return existing;
    }
    existing?.disconnect();

    const url = getEnv('REACT_APP_AI_AGENT_GATEWAY_URL', true);
    if (!url) {
      throw new Error('Agent gateway is not configured');
    }
    const token = await getAccessTokenSilently();

    // The gateway is path-routed at /ai-gateway/* on the shared tenant ALB host
    // (single-name cert → no dedicated subdomain), so the engine.io path is
    // prefixed to match. Must stay in lockstep with the gateway's SOCKET_IO_PATH.
    const socket = io(url, {
      path: '/ai-gateway/socket.io/',
      auth: { token },
    });
    socketRef.current = socket;

    await new Promise<void>((resolve, reject) => {
      socket.once('connect', () => resolve());
      socket.once('connect_error', (err: Error) => {
        socket.disconnect();
        socketRef.current = null;
        reject(err);
      });
    });

    return socket;
  }, [getAccessTokenSilently]);

  const sendAgentMessage = useCallback(
    async (
      content: string,
      isDraftPrompt = false,
      // Snapshotted by the caller at send time (AID-379) — see `usePageContext`.
      // Optional so a turn without page awareness behaves exactly as before.
      pageContext?: PageContext
    ): Promise<void> => {
      const store = useChatStore.getState();
      // Agent mode has no client-visible session id — the gateway's
      // runtimeSessionId is server-only (keyed to the verified user+org) and is
      // never sent to the browser. Mint a per-conversation surrogate (AID-236) so
      // the feedback affordance renders and the feedback pipeline's required
      // sessionId is satisfied. It lives in a DEDICATED `agentSessionId` field,
      // NOT the shared platform `sessionId` — a surrogate left in `sessionId`
      // survives the entitlement-loss flip to platform mode (which doesn't call
      // handleNewChat) and wedges the platform bootstrap (#8443 review). Reset on
      // "New chat" via pendingNewSessionRef (still consumed below for the gateway
      // newSession flag). UI/analytics-only: the eval-metric join is
      // {spanId, traceId}-keyed, so this need not match the LLM-Obs span's session.
      if (!store.agentSessionId || pendingNewSessionRef.current) {
        store.setAgentSessionId(uuidv4());
      }
      // Clear the starter chips on the first send, mirroring the platform send.
      store.clearOptions();
      store.addMessage(content, true);
      store.setIsLoading(true);
      // Cold-start affordance: a thinking step shown BENEATH the user's message
      // (not isInitialising, which would hide the message) until the first event
      // — AgentCore cold start can take a few seconds.
      store.setCurrentThinkingStep('Starting your agent…');

      let socket: Socket;
      try {
        socket = await connect();
      } catch (error) {
        // Surface the real reason (CORS / auth / network) — otherwise a failed
        // connection only shows a generic bubble with nothing in the console.
        console.error('[agent] gateway connection failed', error);
        const s = useChatStore.getState();
        s.setCurrentThinkingStep(null);
        s.setIsLoading(false);
        s.addMessage(agentErrorMessage('agent_unavailable'), false, true);

        return;
      }

      const token = await getAccessTokenSilently();
      let streamingId: string | null = null;

      await new Promise<void>((resolve) => {
        // Open the assistant message on the FIRST event of ANY kind. The agent
        // commonly emits tool_call_* BEFORE any text token (it queries data,
        // then writes the answer), and the store drops tool metadata when there
        // is no streaming message yet — so opening only on the first chunk would
        // lose the tool-call surface for the common case.
        // Returns the message id so handlers that address the message directly
        // (e.g. the prefill proposal) don't have to re-derive it.
        const ensureStreaming = (s: ChatState): string => {
          if (!streamingId) {
            const id = uuidv4();
            streamingId = id;
            s.setCurrentThinkingStep(null);
            s.startStreamingMessage(id);

            return id;
          }

          return streamingId;
        };

        const onChunk = (data: MessageChunkEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.appendToStreamingMessage(data.delta);
        };

        const onThinking = (data: ThinkingEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.pushAgentActivity({
            kind: 'thinking',
            text: data.text,
            redacted: data.redacted,
            parentToolUseId: data.parentToolUseId,
          });
        };

        const onToolStart = (data: ToolCallStartEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.pushAgentActivity({
            kind: 'tool_start',
            toolUseId: data.toolUseId,
            name: data.name,
            input: data.input,
            parentToolUseId: data.parentToolUseId,
            subagentType: data.subagentType,
            // Client receive-time stamp: pairs with tool_complete to derive the
            // per-call duration client-side (AID-367 — not a first-class SDK field).
            startedAt: new Date().toISOString(),
          });
        };

        const onToolComplete = (data: ToolCallCompleteEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.pushAgentActivity({
            kind: 'tool_complete',
            toolUseId: data.toolUseId,
            isError: data.isError,
            result: data.result,
            resultTruncated: data.resultTruncated,
            endedAt: new Date().toISOString(),
          });
        };

        const onToolDenied = (data: ToolCallDeniedEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.pushAgentActivity({
            kind: 'tool_denied',
            toolUseId: data.toolUseId,
            name: data.name,
            reasonCode: data.reasonCode,
            parentToolUseId: data.parentToolUseId,
          });
        };

        // The agent proposes values for the form on screen (AID-379). Attached to
        // the assistant message rather than the activity timeline — it's an
        // actionable card the user applies, not a step to read.
        //
        // `recordId` is taken from the pageContext THIS turn was sent with, not
        // from the live store on arrival (AID-381). The agent proposed against
        // the descriptor snapshotted at send time, so that is the record the
        // values belong to; reading the store here would silently re-point the
        // proposal at whatever form the user navigated to mid-turn, and
        // `ProposalCard`'s staleness guard — which exists to catch exactly that
        // — would see a match and let Apply write into the wrong record.
        const onProposal = (data: FormPrefillProposalEvent) => {
          const s = useChatStore.getState();
          const messageId = ensureStreaming(s);
          const activeForm = pageContext?.activeForm;
          s.setProposal(messageId, {
            proposalId: data.proposalId,
            entityType: data.entityType,
            recordId: activeForm?.recordId ?? null,
            // `recordId: null` alone can't tell a create form from a turn that
            // carried no form at all — see FormPrefillProposal.hasActiveForm.
            hasActiveForm: activeForm !== undefined,
            fields: data.fields,
          });
        };

        const finish = () => {
          const s = useChatStore.getState();
          s.finishStreamingMessage();
          s.setCurrentThinkingStep(null);
          s.setIsLoading(false);
          cleanup();
          cancelTurnRef.current = null;
          resolve();
        };

        const onComplete = (data: MessageCompleteEvent) => {
          // Capture the LLM-Obs ids into the streaming message's metadata BEFORE
          // finishing (AID-236). Require the COMPLETE set (#8443 review): all four
          // ride message_complete together or not at all, and MessageFeedback +
          // ingestion need spanId + mlApp non-empty, so capturing on a partial set
          // would render a control that can't join the recorded span.
          // updateStreamingMetadata no-ops if no message opened (an empty
          // completion has nothing to rate).
          if (data.responseId && data.traceId && data.spanId && data.mlApp) {
            const s = useChatStore.getState();
            s.updateStreamingMetadata({
              responseId: data.responseId,
              traceId: data.traceId,
              spanId: data.spanId,
              // Wire `mlApp` → store `ddApp` (legacy reads `metadata.app` likewise);
              // MessageFeedback submits it as the eval-metric `app` (= ml_app).
              ddApp: data.mlApp,
              // Bind the surrogate to THIS message so its feedback submits under
              // the session it was created in, regardless of a later mode switch
              // (#8443 review — feedback context is message-bound, not mode-bound).
              feedbackSessionId: s.agentSessionId ?? undefined,
            });
          }
          finish();
        };

        const onError = (data: GatewayErrorEvent) => {
          console.error('[agent] gateway error event:', data.error);
          const s = useChatStore.getState();
          if (streamingId) {
            // Record the failure in the activity timeline (AID-368) and flag it
            // inline so a half-finished answer isn't mistaken for a complete one.
            s.pushAgentActivity({ kind: 'turn_error', code: data.error });
            s.appendToStreamingMessage(
              `\n\n_${agentErrorMessage(data.error)}_`
            );
          } else {
            s.addMessage(agentErrorMessage(data.error), false, true);
          }
          finish();
        };

        // A mid-turn disconnect (network blip / server restart / panel close)
        // sends no terminal frame, so without this the promise never settles and
        // the input is wedged on a permanent spinner.
        const onDisconnect = () => {
          console.warn('[agent] gateway socket disconnected mid-turn');
          if (!streamingId) {
            useChatStore
              .getState()
              .addMessage(agentErrorMessage('agent_unavailable'), false, true);
          }
          finish();
        };

        const cleanup = () => {
          socket.off('message_chunk', onChunk);
          socket.off('thinking', onThinking);
          socket.off('tool_call_start', onToolStart);
          socket.off('tool_call_complete', onToolComplete);
          socket.off('tool_call_denied', onToolDenied);
          socket.off('form_prefill_proposal', onProposal);
          socket.off('message_complete', onComplete);
          socket.off('error', onError);
          socket.off('disconnect', onDisconnect);
        };

        socket.on('message_chunk', onChunk);
        socket.on('thinking', onThinking);
        socket.on('tool_call_start', onToolStart);
        socket.on('tool_call_complete', onToolComplete);
        socket.on('tool_call_denied', onToolDenied);
        socket.on('form_prefill_proposal', onProposal);
        socket.on('message_complete', onComplete);
        socket.on('error', onError);
        socket.once('disconnect', onDisconnect);

        // Expose a silent settle path for cancelAgentTurn ("New chat"): reuse
        // finish (which detaches all handlers) so cancelling never routes
        // through onError/onDisconnect and never adds an error message.
        cancelTurnRef.current = finish;

        // Consume the "New chat" flag exactly once, on the turn it applies to.
        const newSession = pendingNewSessionRef.current;
        pendingNewSessionRef.current = false;

        // isDraftPrompt (AID-290) is honoured by the agent only for internal
        // users; the UI only offers the toggle to them, and this is a best-effort
        // request either way.
        // pageContext is optional enrichment: the gateway drops an invalid or
        // oversized one rather than failing the turn (AID-378), so omitting it
        // never costs the user their message.
        socket.emit('send_message', {
          content,
          token,
          isDraftPrompt,
          newSession,
          ...(pageContext ? { pageContext } : {}),
        });
      });
    },
    [connect, getAccessTokenSilently]
  );

  // Tear the dedicated socket down on unmount, and clear any in-flight turn's
  // transient loading state so a reopened panel never inherits a stuck spinner.
  useEffect(
    () => () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
      const s = useChatStore.getState();
      s.setIsLoading(false);
      s.setCurrentThinkingStep(null);
    },
    []
  );

  return {
    sendAgentMessage,
    connectAgent: connect,
    resetAgentSession,
    cancelAgentTurn,
  };
};
